import React, { useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { PerspectiveCamera, Environment, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { NRCFModel } from './NRCFModel';
import { EvaluationModel } from './EvaluationModel';
import { SimulationModel } from './SimulationModel';

export const Scene = ({ onSelect, selectedId, data, mode = 'framework', onShowSimulationDetails }) => {
    const groupRef = useRef();
    const { gl } = useThree();

    // Handle WebGL context loss/restoration
    useEffect(() => {
        const canvas = gl.domElement;

        const handleContextLost = (event) => {
            event.preventDefault();
            console.warn('WebGL context lost. Attempting to restore...');
        };

        const handleContextRestored = () => {
            console.log('WebGL context restored successfully.');
        };

        canvas.addEventListener('webglcontextlost', handleContextLost);
        canvas.addEventListener('webglcontextrestored', handleContextRestored);

        return () => {
            canvas.removeEventListener('webglcontextlost', handleContextLost);
            canvas.removeEventListener('webglcontextrestored', handleContextRestored);
        };
    }, [gl]);

    useFrame((state, delta) => {
        // Animate model position based on selection
        const targetX = selectedId ? -4 : 0;

        if (groupRef.current) {
            // Smooth position transition
            groupRef.current.position.x = THREE.MathUtils.lerp(groupRef.current.position.x, targetX, delta * 4);

            // "Weighing Scale" Movement (Oscillation)
            // Only oscillate if not selected and not in simulation mode
            if (!selectedId && mode !== 'simulation') {
                const t = state.clock.getElapsedTime();
                // Gentle sine wave rotation between -15 and 15 degrees (approx 0.26 rad)
                groupRef.current.rotation.y = Math.sin(t * 0.5) * 0.2;
            } else {
                // Reset rotation when selected
                groupRef.current.rotation.y = THREE.MathUtils.lerp(groupRef.current.rotation.y, 0, delta * 4);
            }
        }
    });

    return (
        <>
            <PerspectiveCamera makeDefault position={[0, 0, 14]} fov={45} />
            <OrbitControls
                enablePan={false}
                enableZoom={true}
                minDistance={5}
                maxDistance={20}
                autoRotate={false} // Disabled auto-rotate in favor of custom oscillation
                dampingFactor={0.05}
                minAzimuthAngle={-Math.PI / 4} // Limit to 45 degrees
                maxAzimuthAngle={Math.PI / 4}
            />

            <ambientLight intensity={0.8} />
            <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
            <pointLight position={[-10, -10, -10]} intensity={0.5} color="white" />
            <Environment preset="city" />

            <group ref={groupRef}>
                {mode === 'framework' && (
                    <NRCFModel onSelect={onSelect} selectedId={selectedId} data={data} />
                )}
                {mode === 'evaluation' && (
                    <EvaluationModel onSelect={onSelect} selectedId={selectedId} data={data} />
                )}
                {mode === 'simulation' && (
                    <SimulationModel onShowDetails={onShowSimulationDetails} />
                )}
            </group>
        </>
    );
};
